import React from 'react'
import { useSelector } from 'react-redux'
import { createUrl } from 'src/modules/utils/createUrl'
import { Link } from 'gatsby'
import { selectAnalyticsParameters } from 'src/modules/reducers/analytics'
import SEO from 'src/components/common/SEO'
import { LogoSyncContextProvider } from 'src/components/common/AnnotationContext'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import {
  ChannelSection,
  DealSection,
  FanduelPlanCardsNew,
  FAQSection,
  HeroSection,
  Layout,
  NetworkSection,
  PlatformSection,
} from 'src/components/common'
import HeroFootNote from 'src/components/common/HeroSectionNew/components/HeroFootNote'
import { PlanCards } from 'src/components/common/PlanCards'
import MatchTicker from 'src/components/common/MatchTicker'

const FuboFreeTemplate = ({
  lang = 'en',
  market = 'us',
  noRedirect,
  miceText,
  // Offer Code
  offerCode,
  offerBanner,
  // Addon Code
  addonCode,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  logo,
  logoHeight,
  title,
  subtitle,
  tagline,
  ctaText,
  video,
  videoMobile,
  staticAnnotation,
  annotations,
  poster,
  posterMobile,
  noteLink,
  heroCtaNote,
  // Network Section
  networkHeadline,
  networkSubtitle,
  networkSet,
  customNetworks,
  // Others
  team,
  // Show offer input for unique codes
  hasOfferInput,
  // AB Test - Video vs Logo Soup
  noVideo = false,
  // Remove references to cable
  removeCable,
  // Charter Plan Cards
  hasCharterPlanCards,

  // Disable Free Trial
  disableFreeTrial,

  // CA Redirect URL
  caRedirectUrl,
  // SVOD Rate Plan
  ratePlan,
  additionalFAQs,
  hasFanduelPlanCards,
  channelTitle,
  fanduelRSNData,
  dealTitle,
  faqListOverride,
  sportId,
  leagueId,
  teamId,
  planCardSubtitle,
  showFanduelWithPro,
  hasPlanCards,
  plans,
  plansSharedData,
  platformImage,
  sportType,
  matchtickerSportId,
  matchtickerLeagueName,
  matchtickerLeagueId,
  maxNetworkCount,
}) => {
  const modifiedCtaText =
    ctaText || (disableFreeTrial && (lang === 'es' ? 'Acceder Ahora' : 'Start Watching'))
  const modifiedNetworkSubtitle =
    networkSubtitle ||
    (disableFreeTrial && (lang === 'es' ? 'Cancela cuando quieras.' : 'Cancel online anytime.'))

  let metaDescription =
    pageDescription ||
    // TEMPORARY NBC DISPUTE
    // 'Stream live TV with ABC, CBS, FOX, NBC, ESPN & top channels without cable. DVR included. No contract, cancel anytime.'
    'Stream live TV with ABC, CBS, FOX, ESPN & top channels without cable. DVR included. No contract, cancel anytime.'

  if (team) {
    // TEMPORARY NBC DISPUTE
    // metaDescription = `Stream ${team} live online with ABC, CBS, FOX, NBC, and ESPN on Fubo. No contract is required - try it for free and cancel anytime.`
    metaDescription = `Stream ${team} live online with ABC, CBS, FOX, and ESPN on Fubo. No contract is required - try it for free and cancel anytime.`
  }

  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const handleLocaleSwitch = () => {
    fireUIInteractionEvent(
      {
        element: noteUrl,
        component: 'lp_locale_switch_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  const originalParams = useSelector(selectAnalyticsParameters)
  let noteUrl
  if (noteLink) {
    noteUrl = createUrl(noteLink, originalParams)
  }

  let noteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        To learn more in English, <span>click here</span>.
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Para traducir a español, <span>haga clic aquí</span>.
      </Link>
    )

  let charterNoteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        English
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Español
      </Link>
    )

  const hasAnchor = hasFanduelPlanCards || hasPlanCards ? '#select-plan' : null

  const hasOffer = offerBanner || offerCode
  if (!hasOffer && fanduelRSNData?.offerCode) {
    offerCode = fanduelRSNData?.offerCode
  }

  const heroFootnote = !disableFreeTrial ? <HeroFootNote lang={lang} /> : null
  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      offerBanner={offerBanner}
      addonCode={addonCode}
      ctaHrefOverride={hasAnchor}
      noteText={noteUrl && hasCharterPlanCards && charterNoteText}
      noRedirect={noRedirect}
      miceText={miceText}
      caRedirectUrl={caRedirectUrl}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
    >
      <SEO lang={lang} market={market} title={pageTitle || title} description={metaDescription} />
      <LogoSyncContextProvider annotations={annotations}>
        {matchtickerLeagueId && (
          <MatchTicker
            lang={lang}
            market={market}
            sportId={matchtickerSportId}
            leagueName={matchtickerLeagueName}
            leagueId={matchtickerLeagueId}
            ctaText={modifiedCtaText}
            disableFreeTrial={disableFreeTrial}
            ctaHrefOverride={hasAnchor}
          />
        )}
        <HeroSection
          logo={logo}
          logoHeight={logoHeight}
          lang={lang}
          market={market}
          heroBgVideo={video}
          heroBgVideoMobile={videoMobile}
          title={title}
          subtitle={subtitle}
          tagline={tagline}
          ctaText={modifiedCtaText}
          staticAnnotation={staticAnnotation}
          posterImg={poster}
          posterImgMobile={posterMobile}
          noteText={heroCtaNote || (noteUrl && !hasCharterPlanCards && noteText)}
          hasOfferInput={hasOfferInput}
          noVideo={noVideo}
          ctaHrefOverride={hasAnchor}
          offerCode={offerCode}
          addonCode={addonCode}
          charterNoteText={noteUrl && hasCharterPlanCards && charterNoteText}
          disableFreeTrial={disableFreeTrial}
          footnote={heroFootnote}
        />
      </LogoSyncContextProvider>

      {hasFanduelPlanCards && (
        <FanduelPlanCardsNew
          rsnData={fanduelRSNData}
          subtitle={planCardSubtitle}
          showFanduelWithPro={showFanduelWithPro}
          sportType={sportType}
        />
      )}

      {hasPlanCards && (
        <PlanCards lang={lang} market={market} plans={plans} sharedData={plansSharedData} />
      )}

      <NetworkSection
        lang={lang}
        market={market}
        headline={networkHeadline || `Stream live TV and sports without cable`}
        subtitle={networkSubtitle || 'No contract. No cable required.'}
        networkSet={networkSet || 'svod'}
        customNetworks={customNetworks}
        ctaText={modifiedCtaText}
        hasOfferInput={hasOfferInput}
        ctaHrefOverride={hasAnchor}
        maxCount={maxNetworkCount || 12}
        disableFreeTrial={disableFreeTrial}
      />
      <ChannelSection
        lang={lang}
        market={market}
        title={channelTitle || 'Plus, stream 200 live TV channels included FREE'}
        ratePlanCode={ratePlan}
        fuboFree={true}
      />

      <DealSection
        lang={lang}
        market={'svod'}
        title={dealTitle || 'Top 3 reasons to stream with Fubo'}
      />

      <PlatformSection
        lang={lang}
        market={market}
        ctaText={modifiedCtaText}
        hasOfferInput={hasOfferInput}
        ctaHrefOverride={hasAnchor}
        title={
          <div>
            Stream live and on <div className="mobile-line-break">demand from anywhere</div>
          </div>
        }
        platformImage={platformImage}
        platformType="svod"
        disableFreeTrial={disableFreeTrial}
      />

      <FAQSection
        lang={lang}
        market={market}
        team={team}
        removeCable={removeCable}
        additionalFAQs={additionalFAQs}
        isSvod={true}
        faqListOverride={faqListOverride}
      />
    </Layout>
  )
}

export default FuboFreeTemplate
